import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { addNotification, NotificationType } from 'stores/notifications/notifications';

import { downloadSsccLabels, printSsccLabels } from '../helpers/ssccLabels';

/**
 * Centralizes the SSCC-label print/download actions so the per-row and bulk buttons share one
 * implementation: each handler takes a list of label URLs (one for a single row, many for "all")
 * and reports its own busy state so the triggering button can show a spinner.
 */
export const useSsccLabels = () => {
  const { t } = useTranslation();
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const run = async (
    pdfUrls: Array<string>,
    action: (urls: Array<string>) => Promise<void>,
    setBusy: (busy: boolean) => void,
  ) => {
    if (!pdfUrls.length) {
      return;
    }
    setBusy(true);
    try {
      await action(pdfUrls);
    } catch {
      addNotification(t('webedi:transport_units.sscc_label.error.text'), NotificationType.error);
    } finally {
      setBusy(false);
    }
  };

  return {
    isPrinting,
    isDownloading,
    print: (pdfUrls: Array<string>) => run(pdfUrls, printSsccLabels, setIsPrinting),
    download: (pdfUrls: Array<string>) => run(pdfUrls, downloadSsccLabels, setIsDownloading),
  };
};
